import React, {useMemo, useRef, useEffect} from "react";
import {NumberInput, Text, HoverCard, Box} from "@mantine/core";
import {IMeasureBloodPressureStep, IMeasureBloodPressureReading} from "../../../types/protocol";
import {IMeasureBloodPressureStepDefinition} from "../../../types/protocolDefinitions";
import {ZoneAwareDateTimePicker} from "@/common/components/ZoneAwareDateTimePicker";
import stepClasses from "../StepView.module.css";
import {DeepPartial} from "@/utils/types/DeepPartial";
import classes from "./MeasureBpStepView.module.css";
import cx from "clsx";
import dayjs from "dayjs";
import {TWorkflow} from "../StepView";

/** Union type for BP measurement steps (both execution and planning modes) */
type BpStep = IMeasureBloodPressureStep | IMeasureBloodPressureStepDefinition;

export interface MeasureBpStepViewProps {
    step: BpStep;
    timezone?: string;
    onChange: (updates: DeepPartial<BpStep>) => void;
    workflow?: TWorkflow;
}

/**
 * Get the status of a reading at a given index
 */
const getReadingStatus = (
    readings: IMeasureBloodPressureReading[],
    index: number,
    isPlanningMode: boolean
): "pending" | "in_progress" | "captured" => {
    if (isPlanningMode) return "pending";

    const reading = readings[index];

    // Check if this reading has been completed
    if (reading && reading.systolic !== undefined && reading.diastolic !== undefined) {
        return "captured";
    }

    // Check if any previous reading is incomplete (blocks this one)
    for (let i = 0; i < index; i++) {
        const prev = readings[i];
        if (!prev || prev.systolic === undefined || prev.diastolic === undefined) {
            return "pending";
        }
    }

    // First reading (index 0) is always in_progress if not captured
    return "in_progress";
};

/**
 * View component for blood pressure measurement steps.
 * Supports multiple readings (configurable via count) with sequential entry.
 * Used in both protocol execution and planning modes.
 */
export const MeasureBpStepView: React.FC<MeasureBpStepViewProps> = ({
                                                                        step,
                                                                        timezone,
                                                                        onChange,
                                                                        workflow = "session",
                                                                    }) => {
    const isPlanningMode = workflow === "planning";
    const isReadOnly = workflow === "review";

    // Refs for sequential locking UI
    const readingsRef = useRef<IMeasureBloodPressureReading[]>([]);

    // Refs for input focus management (avoiding DOM queries with data-testid)
    const systolicRefs = useRef<(HTMLInputElement | null)[]>([]);
    const diastolicRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Keep refs in sync with step prop
    useEffect(() => {
        if (!isPlanningMode) {
            readingsRef.current = (step as IMeasureBloodPressureStep).readings || [];
        }
    }, [step, isPlanningMode]);

    // Get count from step
    const count = isPlanningMode
        ? (step as IMeasureBloodPressureStepDefinition).count || 3
        : (step as IMeasureBloodPressureStep).count || 1;

    // Get readings from step (for execution mode)
    const readings = isPlanningMode
        ? []
        : (step as IMeasureBloodPressureStep).readings || [];

    // Generate readings array for rendering
    const readingsToRender = useMemo(() => {
        if (isPlanningMode) {
            return Array.from({length: count}, (_, i) => ({
                index: i,
                reading: {systolic: undefined, diastolic: undefined, capturedAt: undefined},
                status: "pending" as const
            }));
        }

        return Array.from({length: count}, (_, i) => ({
            index: i,
            reading: readingsRef.current[i] || readings[i] || {
                systolic: undefined,
                diastolic: undefined,
                capturedAt: undefined
            },
            status: getReadingStatus(readingsRef.current.length ? readingsRef.current : readings, i, isPlanningMode)
        }));
    }, [count, readings, isPlanningMode]);

    // Handle count blur - read value directly from DOM
    const handleCountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.currentTarget.value === "") {
            e.currentTarget.value = count.toString()
        } else {
            const numCount = parseInt(e.currentTarget.value, 10);
            if (isPlanningMode && numCount > 0) {
                onChange({count: numCount} as DeepPartial<IMeasureBloodPressureStepDefinition>);
            }
        }
    };

    // Handle blur - commit value and capture timestamp if both exist
    const handleBlur = (index: number, type: "systolic" | "diastolic", e: React.FocusEvent<HTMLInputElement>) => {
        const inputValue = e.currentTarget.value;
        const numValue = parseInt(inputValue, 10);
        const safeValue = isNaN(numValue) ? undefined : numValue;

        // Update ref
        const updatedReadings = [...readingsRef.current];
        if (!updatedReadings[index]) {
            updatedReadings[index] = {systolic: undefined, diastolic: undefined, capturedAt: undefined};
        }

        // Compute the merged reading BEFORE deciding on capturedAt — the
        // typed value is part of "both filled" for the next state.
        const newReading = {
            ...updatedReadings[index],
            [type]: safeValue,
        }
        updatedReadings[index] = {
            ...newReading,
            capturedAt:
                newReading?.systolic !== undefined && newReading?.diastolic !== undefined
                    ? dayjs().toISOString()
                    : undefined
        }
        readingsRef.current = updatedReadings;
        onChange({readings: updatedReadings} as DeepPartial<IMeasureBloodPressureStep>);
    };

    // Handle Tab key on inputs
    const handleKeyDown = (index: number, type: "systolic" | "diastolic", e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.shiftKey && e.key === "Tab") {
            // Shift+Tab
            e.preventDefault();
            e.currentTarget.blur();
            setTimeout(() => {
                if (type === "systolic") {
                    // Shift+Tab on systolic: go to previous diastolic or stay if first
                    if (index > 0) {
                        diastolicRefs.current[index - 1]?.focus();
                    }
                } else {
                    // Shift+Tab on diastolic: go to systolic of same reading
                    systolicRefs.current[index]?.focus();
                }
            }, 0);
        } else if (e.key === "Tab" && !e.shiftKey) {
            // Tab
            e.preventDefault();

            // Commit value directly (blur event may not fire in jsdom when testing)
            const inputValue = e.currentTarget.value;
            const numValue = parseInt(inputValue, 10);
            const safeValue = isNaN(numValue) ? undefined : numValue;

            const updatedReadings = [...readingsRef.current];
            if (!updatedReadings[index]) {
                updatedReadings[index] = {systolic: undefined, diastolic: undefined, capturedAt: undefined};
            }
            updatedReadings[index] = {
                ...updatedReadings[index],
                [type]: safeValue
            };
            readingsRef.current = updatedReadings;
            onChange({readings: updatedReadings} as DeepPartial<IMeasureBloodPressureStep>);

            e.currentTarget.blur();
            setTimeout(() => {
                if (type === "systolic") {
                    // Tab on systolic: go to diastolic of same reading
                    diastolicRefs.current[index]?.focus();
                } else {
                    // Tab on diastolic: go to next systolic or stay if last
                    if (index < count - 1) {
                        systolicRefs.current[index + 1]?.focus();
                    }
                }
            }, 0);
        }
    };

    // Render value cells with timestamp picker in HoverCard
    const renderValueCells = () => {
        return readingsToRender.map(({index, reading, status}) => {
            const isCaptured = reading.systolic !== undefined && reading.diastolic !== undefined;
            const isLocked = status === "pending";

            const cellClass = cx(
                classes.valueCell,
                isCaptured && classes.captured,
                status === "in_progress" && classes.inProgress
            );

            return (
                <Box key={`value-${index}`} className={cellClass}>
                    {/* HoverCard trigger covers just the header area */}
                    {isCaptured && reading.capturedAt ? (
                        <HoverCard
                            shadow="md"
                            openDelay={250}
                            closeDelay={250}
                            withArrow
                            position="top"
                        >
                            <HoverCard.Target>
                                <Box className={classes.timestampTrigger}>
                                    <Text className={classes.cellHeader}>Reading {index + 1}</Text>
                                </Box>
                            </HoverCard.Target>
                            <HoverCard.Dropdown>
                                <ZoneAwareDateTimePicker
                                    timezone={timezone!}
                                    label={"Captured At"}
                                    value={dayjs(reading.capturedAt)}
                                    onChange={(value) => {
                                        const currentReadings = readingsRef.current;
                                        const updatedReadings = [...currentReadings];
                                        updatedReadings[index] = {
                                            ...updatedReadings[index],
                                            capturedAt: value?.toISOString()
                                        };
                                        readingsRef.current = updatedReadings;
                                        onChange({readings: updatedReadings} as DeepPartial<IMeasureBloodPressureStep>);
                                    }}
                                    dateTimeProps={{
                                        size: "xs",
                                        "data-testid": `time-input-capturedAt-${index}`,
                                    }}
                                />
                            </HoverCard.Dropdown>
                        </HoverCard>
                    ) : (
                        <Text className={classes.cellHeader}>Reading {index + 1}</Text>
                    )}
                    <Box className={classes.inputWrapper}>
                        <NumberInput
                            className={classes.bpInput}
                            data-testid={`input-systolic-${index}`}
                            size="xs"
                            placeholder="Sys"
                            defaultValue={reading.systolic}
                            allowNegative={false}
                            hideControls
                            disabled={isLocked || isReadOnly}
                            onKeyDown={(e) => handleKeyDown(index, "systolic", e)}
                            onBlur={(e) => handleBlur(index, "systolic", e)}
                            ref={(el) => {
                                systolicRefs.current[index] = el;
                            }}
                        />
                        <Text className={classes.separator}>/</Text>
                        <NumberInput
                            className={classes.bpInput}
                            data-testid={`input-diastolic-${index}`}
                            size="xs"
                            placeholder="Dia"
                            defaultValue={reading.diastolic}
                            allowNegative={false}
                            hideControls
                            disabled={isLocked || isReadOnly}
                            onKeyDown={(e) => handleKeyDown(index, "diastolic", e)}
                            onBlur={(e) => handleBlur(index, "diastolic", e)}
                            ref={(el) => {
                                diastolicRefs.current[index] = el;
                            }}
                        />
                    </Box>
                </Box>
            );
        });
    };

    return (
        <Box style={{display: "grid", grid: "auto / 1fr", width: "100%", gridColumn: "1 / -1"}}>
            <Box style={{display: "grid", grid: " auto / auto 1fr", columnGap: "1em"}}>
                {/* Planning Mode: Count Input */}
                {isPlanningMode ? (
                    <div className={stepClasses.stepViewField}>
                        <Text size="sm" mb="xs">Number of readings:</Text>
                        <NumberInput
                            className={classes.countInput}
                            data-testid="input-count"
                            size="xs"
                            value={count}
                            min={1}
                            max={10}
                            clampBehavior={"strict"}
                            allowNegative={false}
                            hideControls
                            disabled={isReadOnly}
                            onBlur={handleCountBlur}
                        />
                    </div>
                ) : <Box/>}

                {/* Readings Grid - Tabular layout */}
                <Box className={classes.readingsGrid}>
                    {renderValueCells()}
                </Box>
            </Box>
        </Box>
    );
};